import { Plus } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { suppliersAdminApi, supplierKeys, type SupplierAdminRow } from "@/lib/suppliers";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntentButton } from "@/components/intent-button";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const supplierSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  code: z.string().nullable(),
  publicUrl: z.string().nullable(),
});

type SupplierForm = z.infer<typeof supplierSchema>;

const BLANK_FORM: SupplierForm = {
  name: "",
  code: null,
  publicUrl: null,
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function SuppliersAdminPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SupplierAdminRow | null>(null);

  // List (0-based paging — first page page=0)
  const { data, isLoading } = useQuery({
    queryKey: supplierKeys.adminList(q, page),
    queryFn: () => suppliersAdminApi.listAdmin(q, page),
  });

  // Create / update mutation
  const upsert = useMutation({
    mutationFn: (form: SupplierForm) => {
      const body = {
        name: form.name,
        code: form.code ? form.code : null,
        publicUrl: form.publicUrl ? form.publicUrl : null,
      };
      if (editing) {
        return suppliersAdminApi.update(editing.supplierId, body, editing.rowVersionBase64);
      }
      return suppliersAdminApi.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers", "admin"] });
      setCreating(false);
      setEditing(null);
    },
    onError: (err: Error) => {
      toast.error(`Save failed: ${err.message}`);
    },
  });

  // Delete mutation
  const remove = useMutation({
    mutationFn: (row: SupplierAdminRow) =>
      suppliersAdminApi.remove(row.supplierId, row.rowVersionBase64),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers", "admin"] });
    },
    onError: (err: Error) => {
      toast.error(`Cannot delete: ${err.message}`);
    },
  });

  // Columns
  const cols: DataGridColumn<SupplierAdminRow>[] = [
    { id: "name", header: "Name", sortable: true, accessor: (r) => r.name },
    { id: "code", header: "Code", accessor: (r) => r.code ?? "—" },
    { id: "publicUrl", header: "Public URL", accessor: (r) => r.publicUrl ?? "—" },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex gap-1">
          <IntentButton intent="ghost" onClick={() => setEditing(r)}>
            Edit
          </IntentButton>
          <IntentButton intent="ghost" onClick={() => remove.mutate(r)}>
            Delete
          </IntentButton>
        </div>
      ),
    },
  ];

  // Default form values when editing
  function toFormValues(row: SupplierAdminRow): SupplierForm {
    return {
      name: row.name,
      code: row.code,
      publicUrl: row.publicUrl,
    };
  }

  const drawerOpen = creating || editing !== null;
  const drawerTitle = editing ? "Edit supplier" : "New supplier";

  return (
    <ListPage backTo="/admin"
      title="Suppliers"
      primaryAction={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Supplier</Button>}
      search={{
        value: q,
        onChange: (v) => {
          setQ(v);
          setPage(0);
        },
        placeholder: "Search name or code…",
      }}
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.supplierId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={drawerTitle}
      >
        <EntityForm
          schema={supplierSchema}
          defaultValues={editing ? toFormValues(editing) : BLANK_FORM}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              {/* Name */}
              <div className="space-y-1">
                <Label htmlFor="sup-name">Name</Label>
                <Input id="sup-name" {...form.register("name")} />
              </div>

              {/* Code */}
              <div className="space-y-1">
                <Label htmlFor="sup-code">Code</Label>
                <Input id="sup-code" {...form.register("code")} />
              </div>

              {/* Public URL */}
              <div className="space-y-1">
                <Label htmlFor="sup-url">Public URL</Label>
                <Input id="sup-url" {...form.register("publicUrl")} />
              </div>

              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
